import { Component, OnInit, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { SharedSignalsService } from '../../common/services/shared-signals.service';
import { PartnerService } from '../service/partner.service';
import { regexConstant } from '../../common/constants/regex-pattern.constant';
import { Country } from '../../common/models/country.model';
import { Category } from '../../common/models/category.model';
import { BusinessType } from '../../common/models/business-type.model';
import { Platform } from '../../common/models/platform.model';
import { Observable, catchError, forkJoin, map, of, tap } from 'rxjs';

type ApplicationForm = FormGroup<{
  businessName: FormControl<string | null>;
  country: FormControl<Country | null>;
  category: FormControl<Category | null>;
  businessType: FormControl<BusinessType | null>;
  mobileNumber: FormControl<string | null>;
  platform: FormControl<Platform | null>;
  webURL: FormControl<string | null>;
  emailAddress: FormControl<string | null>;
  comments: FormControl<string | null>;
  otherPlatform: FormControl<string | null>;
  otherIndustry: FormControl<string | null>;
  contactPersonFullName: FormControl<string | null>;
  brandName: FormControl<string | null>;
  bankName: FormControl<string | null>;
  bankAccountHolderName: FormControl<string | null>;
  bankAccountNumber: FormControl<string | null>;
  iban: FormControl<string | null>;
  logoFile: FormControl<string | null>;
  featuredImage: FormControl<string | null>;
  isShareHolder: FormControl<boolean | null>;
}>;

@Component({
  selector: 'taly-complete-application',
  templateUrl: './complete-application.component.html',
  styleUrls: ['./complete-application.component.scss'],
  standalone: false,
})
export class CompleteApplicationComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private partnerService = inject(PartnerService);
  private messageService = inject(MessageService);

  transferSignal = inject(SharedSignalsService);
  applicationForm!: ApplicationForm;

  // Route params
  token: string = '';
  applicationId: number | null = null;

  // Dropdown data
  countries: any[] = [];
  businessTypes: any[] = [];
  categories: any[] = [];
  platforms: any[] = [];

  // UI state
  textDirection: string = 'ltr';
  loading: boolean = true;
  saving: boolean = false;
  submitted: boolean = false;
  activeStep: number = 0;
  applicationData: any = null;

  // Requested fields from backend
  requestedFields: string[] = [];
  requestedChanges: string = '';

  // Image handling (matching edit-merchant pattern)
  logoFile!: string; // Base64 data for preview and API
  featuredImageFile!: string; // Base64 data for preview and API
  logoFileURL!: string; // URL from API for display
  featuredImageFileURL!: string; // URL from API for display
  maxFileSize: number = 500 * 1024; // 500KB

  // Documents
  documents: any[] = [];
  isAnyDocumentUploaded: boolean = false;

  constructor() {
    effect(() => {
      this.textDirection = this.transferSignal.textDirection();
    });
  }

  ngOnInit(): void {
    this.initForm();
    // Token is passed as query parameter: /complete-application?token=xxx
    this.token = this.route.snapshot.queryParamMap.get('token') || '';

    if (!this.token) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid application link',
      });
      this.router.navigate(['/']);
      return;
    }

    this.loadApplicationData();
  }

  private initForm(): void {
    this.applicationForm = this.fb.group({
      businessName: ['', [Validators.required, Validators.minLength(3)]],
      country: this.fb.control<Country | null>(null),
      category: this.fb.control<Category | null>(null),
      businessType: this.fb.control<BusinessType | null>(null),
      mobileNumber: this.fb.control<string | null>(null, [
        Validators.pattern(regexConstant.KUWAIT_MOBILE_NUMBER),
      ]),
      platform: this.fb.control<Platform | null>(null),
      webURL: this.fb.control<string | null>(null),
      emailAddress: ['', [Validators.email]],
      comments: this.fb.control<string | null>(null, [Validators.maxLength(256)]),
      otherPlatform: [''],
      otherIndustry: [''],
      contactPersonFullName: [''],
      brandName: [''],
      bankName: [''],
      bankAccountHolderName: [''],
      bankAccountNumber: [''],
      iban: [''],
      logoFile: [''], // For error handling (matching edit-merchant pattern)
      featuredImage: [''], // For error handling (matching edit-merchant pattern)
      isShareHolder: [false],
    });
  }

  // Form controls getter (matching edit-merchant pattern)
  get f() {
    return this.applicationForm.controls;
  }

  /** Loads the dropdown lookups; a failed lookup leaves that list empty instead of failing all. */
  private loadDropdownData(): Observable<void> {
    const active = (res: any[] | null | undefined) =>
      res?.filter((elem: any) => elem?.status === '1') || [];
    const orEmpty = <T>(source: Observable<T>) => source.pipe(catchError(() => of(null)));

    return forkJoin({
      countries: orEmpty(this.partnerService.getCountries()),
      businessTypes: orEmpty(this.partnerService.getBusinessType()),
      categories: orEmpty(this.partnerService.getCategories()),
      platforms: orEmpty(this.partnerService.getPlatform()),
    }).pipe(
      tap((res: any) => {
        this.countries = active(res.countries);
        this.businessTypes = active(res.businessTypes);
        this.categories = active(res.categories);
        this.platforms = active(res.platforms);
      }),
      map(() => undefined)
    );
  }

  private loadApplicationData(): void {
    this.loading = true;
    // Load the lookups and the application in parallel, but populate the form only once both
    // are in, because populateForm matches the application against the lookup lists.
    forkJoin({
      data: this.partnerService.getApplicationByToken(this.token),
      lookups: this.loadDropdownData(),
    }).subscribe({
      next: ({ data }) => {
        this.applicationData = data;
        this.applicationId = data?.id;
        this.requestedFields = data?.requestedFields || [];
        this.requestedChanges = data?.requestedChanges || '';
        this.populateForm(data);
        this.loadDocuments();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        const message = err?.error?.message || 'Failed to load application';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
        });
      },
    });
  }

  private populateForm(data: any): void {
    // Find matching dropdown values
    // Country uses isoCode as identifier (not id like other entities)
    const country = this.countries.find((c) => c.isoCode === data.countryId);
    const businessType = this.businessTypes.find((b) => b.nameEn === data.businessType);
    // `categoryId` is the category; older responses only had `industryId` for it.
    const categoryId = data.categoryId ?? data.industryId;
    const category = this.categories.find((c) => c.id === categoryId);
    const platform = this.platforms.find((p) => p.id === data.platfomId);

    this.applicationForm.patchValue({
      businessName: data.businessName || '',
      country: country || null,
      category: category || null,
      businessType: businessType || null,
      mobileNumber: data.contactNumber || null,
      platform: platform || null,
      webURL: data.url || null,
      emailAddress: data.businessEmail || '',
      comments: data.comments || null,
      otherPlatform: data.otherPlatfom || '',
      otherIndustry: data.otherIndustry || '',
      contactPersonFullName: data.contactPersonFullName || '',
      brandName: data.brandName || '',
      bankName: data.bankName || '',
      bankAccountHolderName: data.bankAccountHolderName || '',
      bankAccountNumber: data.bankAccountNumber || '',
      iban: data.iban || '',
      isShareHolder: data.isShareholdingCompany || false,
    });

    // Set image URLs from API response (matching edit-merchant pattern)
    this.logoFileURL = data?.logoUrl;
    this.featuredImageFileURL = data?.featuredImageUrl;
  }

  private loadDocuments(): void {
    if (!this.token) return;

    this.partnerService.getDocumentsByToken(this.token).subscribe({
      next: (docs) => {
        this.documents = docs || [];
        this.isAnyDocumentUploaded = this.documents.length > 0;
      },
      error: (err) => {
        console.error('Error loading documents:', err);
      },
    });
  }

  /** Check if a field should be shown (is in requestedFields) */
  isFieldRequested(fieldName: string): boolean {
    return this.requestedFields.includes(fieldName);
  }

  /** Check if any of the given fields are requested */
  hasAnyRequestedField(fieldNames: string[]): boolean {
    return fieldNames.some((field) => this.requestedFields.includes(field));
  }

  /** Check if documents are requested */
  get hasDocumentRequests(): boolean {
    return this.requestedFields.some((field) => field.startsWith('document_'));
  }

  /** Get requested document types */
  get requestedDocumentTypes(): string[] {
    return this.requestedFields
      .filter((field) => field.startsWith('document_'))
      .map((field) => field.replace('document_', ''));
  }

  // Image handling methods (matching edit-merchant pattern)
  onFileSelected(event: Event, type: 'logoFile' | 'featuredImage'): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) return;

    const fileSizeKB = file.size / 1024;

    // Clear previous errors
    this.applicationForm.get(type)?.setErrors(null);

    // Validate file type
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      this.applicationForm.get(type)?.setErrors({
        fileType: {
          fileType: 'Invalid file type. Please select a PNG or JPG file.',
        },
      });
      return;
    }

    // Validate file size (500KB max)
    if (fileSizeKB > 500) {
      this.applicationForm.get(type)?.setErrors({
        fileSizeExceeded: {
          fileSizeExceeded: 'File exceeds 500KB limit. Please select a smaller file.',
        },
      });
      return;
    }

    // Convert to base64 for preview and API
    this.convertToBase64(file, type);
  }

  convertToBase64(file: File, type: 'logoFile' | 'featuredImage'): void {
    const reader = new FileReader();
    reader.onload = () => {
      if (type === 'logoFile') {
        this.logoFile = reader.result as string;
      } else {
        this.featuredImageFile = reader.result as string;
      }
    };
    reader.readAsDataURL(file);
  }

  // Strip base64 prefix before sending to API (matching edit-merchant's replaceImg)
  replaceImg(img: string): string {
    return img?.replace(img?.slice(0, img?.indexOf(',') + 1), '').trim();
  }

  onDocumentUpload(event: any, documentType: string): void {
    const file = event.files?.[0];
    if (!file || !this.applicationId) return;

    this.partnerService
      .uploadDocuments(this.applicationId, documentType, file, {
        completionToken: this.token,
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Uploaded',
            detail: 'Document uploaded successfully',
          });
          this.loadDocuments();
        },
        error: (err) => {
          const message = err?.error?.message || 'Upload failed';
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: message,
          });
        },
      });
  }

  deleteDocument(documentId: number): void {
    if (!this.applicationId) return;

    this.partnerService.deleteDocument(this.applicationId, documentId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Deleted',
          detail: 'Document removed successfully',
        });
        this.loadDocuments();
      },
      error: (err) => {
        const message = err?.error?.message || 'Delete failed';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
        });
      },
    });
  }

  onSubmit(): void {
    this.saving = true;

    // Build request body with ONLY the requested fields
    const formValue = this.applicationForm.value;
    const requestBody: any = {};

    // Map requested fields to their form values
    for (const field of this.requestedFields) {
      // Skip document fields (handled separately)
      if (field.startsWith('document_')) continue;

      switch (field) {
        case 'businessName':
          requestBody.businessName = formValue.businessName?.trim();
          break;
        case 'brandName':
          requestBody.brandName = formValue.brandName?.trim();
          break;
        case 'businessEmail':
          requestBody.businessEmail = formValue.emailAddress;
          break;
        case 'contactNumber':
          requestBody.contactNumber = formValue.mobileNumber;
          break;
        case 'contactPersonFullName':
          requestBody.contactPersonFullName = formValue.contactPersonFullName?.trim();
          break;
        case 'countryId':
          // Country uses isoCode as identifier (not id like other entities)
          requestBody.countryId = formValue.country?.isoCode;
          requestBody.countryNameEn = formValue.country?.nameEn?.trim();
          break;
        case 'industryId':
        case 'categoryId':
          requestBody.categoryId = formValue.category?.id;
          requestBody.categoryNameEn = formValue.category?.nameEn?.trim();
          break;
        case 'businessType':
          requestBody.businessType = formValue.businessType?.nameEn?.trim();
          requestBody.industryId = formValue.businessType?.id;
          break;
        case 'platfomId':
        case 'platform':
          requestBody.platfomId = formValue.platform?.id;
          requestBody.platformNameEn = formValue.platform?.nameEn?.trim();
          break;
        case 'url':
          requestBody.url = formValue.webURL;
          break;
        case 'otherPlatfom':
          requestBody.otherPlatfom = formValue.otherPlatform;
          break;
        case 'otherIndustry':
          requestBody.otherIndustry = formValue.otherIndustry;
          break;
        case 'bankName':
          requestBody.bankName = formValue.bankName?.trim();
          break;
        case 'bankAccountHolderName':
          requestBody.bankAccountHolderName = formValue.bankAccountHolderName?.trim();
          break;
        case 'bankAccountNumber':
          requestBody.bankAccountNumber = formValue.bankAccountNumber?.trim();
          break;
        case 'iban':
          requestBody.iban = formValue.iban?.trim();
          break;
        // Note: logoUrl and featuredImageUrl are handled outside this loop
        // to ensure images are always sent if they exist
        case 'comments':
          requestBody.comments = formValue.comments;
          break;
      }
    }

    // Always include images if they exist (outside the requestedFields loop)
    // This ensures images are sent even if not in the original requestedFields
    if (this.logoFile) {
      requestBody.logoFile = this.replaceImg(this.logoFile);
    }
    if (this.featuredImageFile) {
      requestBody.featuredImageFile = this.replaceImg(this.featuredImageFile);
    }

    this.partnerService.completeApplication(this.token, requestBody).subscribe({
      next: () => {
        this.saving = false;
        // Go to thank you screen
        this.finalSubmit();
      },
      error: (err) => {
        this.saving = false;
        const message = err?.error?.message || 'Failed to submit changes';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
        });
      },
    });
  }

  /** Submit all changes (fields + documents) in one action */
  onSubmitAll(): void {
    // Always call completeApplication API to transition status from NEEDS_CHANGES to PENDING
    // Even if only documents were requested, the API call is required to update the status
    this.onSubmit();
  }

  finalSubmit(): void {
    this.submitted = true;
    this.messageService.add({
      severity: 'success',
      summary: 'Submitted',
      detail: 'Your application has been submitted for review',
    });
  }

  get isOnlineBusiness(): boolean {
    const businessType = this.applicationForm.get('businessType')?.value?.nameEn;
    return businessType === 'Online' || businessType === 'Both';
  }

  get isOtherPlatform(): boolean {
    return this.applicationForm.get('platform')?.value?.nameEn === 'Other';
  }

  get isOtherIndustry(): boolean {
    return this.applicationForm.get('category')?.value?.nameEn === 'Other';
  }

  /** Get display-friendly status name */
  getStatusDisplayName(status: string): string {
    const statusNames: { [key: string]: string } = {
      PENDING: 'Pending',
      PENDING_REVIEW: 'Pending Review',
      UNDER_REVIEW: 'Under Review',
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      CHANGES_REQUESTED: 'Changes Requested',
      NEEDS_CHANGES: 'Changes Requested',
      ONBOARDED: 'Onboarded',
    };
    return statusNames[status] || status;
  }

  /** Get display-friendly field name */
  getFieldDisplayName(field: string): string {
    // Handle document types first
    if (field.startsWith('document_')) {
      return this.getDocumentDisplayName(field.replace('document_', ''));
    }

    const displayNames: { [key: string]: string } = {
      businessName: 'Business Name',
      brandName: 'Brand Name',
      businessEmail: 'Email Address',
      contactNumber: 'Phone Number',
      contactPersonFullName: 'Contact Person',
      countryId: 'Country',
      industryId: 'Category',
      categoryId: 'Category',
      businessType: 'Business Type',
      platfomId: 'Platform',
      url: 'Website URL',
      otherPlatfom: 'Other Platform',
      otherIndustry: 'Other Category',
      bankName: 'Bank Name',
      bankAccountHolderName: 'Account Holder Name',
      bankAccountNumber: 'Account Number',
      iban: 'IBAN',
      logoUrl: 'Logo',
      featuredImageUrl: 'Featured Image',
      comments: 'Comments',
    };
    return displayNames[field] || field;
  }

  /** Get display-friendly document type name */
  getDocumentDisplayName(docType: string | null | undefined): string {
    if (!docType) {
      return '';
    }

    const documentNames: { [key: string]: string } = {
      COMMERCIAL_LICENSE: 'Commercial License',
      COMMERCIAL_REGISTRATION_EXTRACT: 'Commercial Registration Extract',
      IBAN_CERTIFICATE: 'IBAN Certificate',
      CIVIL_ID_COPY: 'Civil ID Copy',
      BOARD_DIRECTOR_CERTIFICATE: 'Board of Directors Certificate',
      SHAREHOLDERS_REGISTER_CERTIFICATE: 'Shareholders Register Certificate',
      MOCI_AUTHORIZED_SIGNATORIES_CERTIFICATE: 'Authorized Signatories Certificate',
    };
    // If not found, convert SNAKE_CASE to Title Case
    return (
      documentNames[docType] ||
      docType
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    );
  }
}
